import jwtDecode from "jwt-decode";
import { KeycloakAuthorizationInstance } from "keycloak-js/dist/keycloak-authz";
import React from "react";
import { useKeycloak } from "../components/keycloak-provider";
import { AI_USER } from "./roles";
import { useRoleChecker } from "../components/authorization-verifier";

export const BACKOFFICE_RESOURCE = "backoffice";
export const ACCESS_DENIED = new Error("Access Denied.");

export async function getPropertyRpt(
  property: string,
  authz: KeycloakAuthorizationInstance
): Promise<string> {
  authz.rpt = null;
  return await new Promise<string>((resolve, reject) =>
    authz
      .entitlement(BACKOFFICE_RESOURCE, {
        permissions: [
          {
            id: `property:${property}`,
          },
        ],
      })
      .then(
        rpt => resolve(rpt),
        () => reject(ACCESS_DENIED),
        () => reject(new Error("authz error"))
      )
  );
}

export interface IUserAuthInfo {
  isWitAdmin: boolean;
  isCRMReadOnly: boolean;
  isC2tAdmin: boolean;
  isCampingAdmin: boolean;
  isCampingFrAdmin: boolean;
  isAciAdmin: boolean;
  isCRMAdmin: boolean;
  isAIUser: boolean;
}

export function useUserAuthInfo(): IUserAuthInfo | undefined {
  const keycloak = useKeycloak();
  const isAIUser = useRoleChecker([AI_USER]);
  if (!keycloak.tokenParsed) {
    return undefined;
  }
  const roles = keycloak.tokenParsed.realm_access?.roles || [];

  return {
    isWitAdmin: roles.indexOf("witbooking-admin") !== -1,
    isCRMReadOnly: roles.indexOf("crm-read-only") !== -1,
    isC2tAdmin: roles.indexOf("c2t-admin") !== -1,
    isCampingAdmin: roles.indexOf("camping-admin") !== -1,
    isCampingFrAdmin: roles.indexOf("camping-fr-admin") !== -1,
    isAciAdmin: roles.indexOf("aci-admin") !== -1,
    isCRMAdmin: roles.indexOf("crm-admin") !== -1,
    isAIUser,
  };
}

export function withUserAuthInfo<P = {}>(
  Component: React.ComponentType<P & { userInfo: IUserAuthInfo }>
): React.ComponentType<P> {
  return (props: P) => {
    const info = useUserAuthInfo();
    return <>{info && <Component userInfo={info} {...props} />}</>;
  };
}

export function decodeRptToken(token: string): IRptToken | undefined {
  try {
    return jwtDecode(token);
  } catch (e) {
    console.error("Error decoding JWT token", e);
    return undefined;
  }
}

export function getPropertyFromRptToken(token: string): string | undefined {
  const decoded = decodeRptToken(token);
  if (!decoded || !decoded.authorization || !decoded.authorization.permissions) {
    return undefined;
  }

  const propertyTickers = decoded.authorization.permissions.map(p =>
    p.rsname.replace("property:", "")
  );
  return propertyTickers.length > 0 ? propertyTickers[0] : undefined;
}

export function isJWTTokenExpired(token: string): boolean {
  const decoded = jwtDecode(token) as any;
  return decoded.exp * 1000 < Date.now();
}

export interface IRptToken {
  email: string;
  authorization: {
    permissions?: IPermission[];
  };
}

export interface IPermission {
  scopes: string[];
  rsid: string;
  rsname: string;
}
